import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Container, Paper, useWindowSize } from 'library';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { NUMBER_FORMATS } from 'utils/formatters';
import { setUserPropertiesForPortals } from 'utils/urlUtilities';
import useIsVisible from 'utils/useIsVisible';
import useLocalState from 'utils/useLocalState';

import DebtCTA from '../components/debtCTA/DebtCTA';
import DebtDisclaimer from '../components/debtDisclaimer/DebtDisclaimer';
import DebtResults from '../components/debtResults/DebtResults';
import TwoColumnChart from '../components/TwoColumnDebtChart/TwoColumnDebtChart';
import DebtFormController from '../DebtFormController';
import { calculatePayoffDate, totalDebtCalculations } from '../utils/debtCalculations';
import { trackFinishClick, trackRemoveDebt } from '../utils/debtCalculatorTrack';

import './DebtCalculatorController.scss';

const DebtCalculatorController = ({
  heading = 'How much is your debt actually costing you?',
  subheading = `The average American has over $90,000 of revolving debt from credit cards, mortgages, student loans, and personal loans. But most people don't realize how much they're paying in interest. Use our debt calculator below to see how much your debt might actually be costing you:`,
}) => {
  const { isScreenSize } = useWindowSize();
  const { ref, isVisible } = useIsVisible({ threshold: [0, 1] });
  const [isFinished, onFinish] = useState(false);
  const [resultsCookie, setResultsCookie] = useQueryParam('results', BooleanParam);
  const [isEditing, setIsEditing] = useState(false);
  const [debts, setDebts] = useLocalState([], 'DebtCalculatorDebts');
  const [lastUpdatedDebts, setLastUpdatedDebts] = useLocalState(null, 'LastUpdatedDebts');
  const {
    totalBalance,
    paymentsToZero,
    totalInterestPayments,
    totalMonthlyPayment,
    totalPrincipalBalance,
  } = useMemo(() => totalDebtCalculations(debts), [debts]);

  const payOffDate = useMemo(() => calculatePayoffDate(paymentsToZero), [paymentsToZero]);

  const isIndex = !debts.length;
  const onDebtSubmit = (debtInfo) => {
    setIsEditing(false);
    setDebts([...debts, debtInfo]);
    setLastUpdatedDebts(new Date());
  };

  const onDebtDelete = (index) => {
    const newDebtList = [...debts].filter((debt, debtToDelete) => index !== debtToDelete);
    const data = { ...debts[index], debts };
    trackRemoveDebt(data);
    setDebts(newDebtList);
    setLastUpdatedDebts(new Date());
  };

  const handleFinishClick = () => {
    setResultsCookie(true);
    trackFinishClick({
      debts,
      totalBalance,
      totalInterestPayments,
      totalMonthlyPayment,
      payOffDate,
      totalPrincipalBalance,
    });
    onFinish(true);
    setUserPropertiesForPortals({
      'Debt Calculator Total Debts': debts.length,
      'Debt Calculator Monthly Payments': NUMBER_FORMATS.currency(totalMonthlyPayment, 0),
      'Debt Calculator Total Interest': NUMBER_FORMATS.currency(totalInterestPayments, 0),
      'Debt Calculator Total Balance': NUMBER_FORMATS.currency(totalBalance, 0),
      'Debt Calculator Debt Free Date': payOffDate,
      'Debt Calculator Debt Types': debts.map((debt) => debt.typeOfDebt).join(','),
    });
  };

  // if local storage debts are more than 24 hours old delete them.
  useEffect(() => {
    const yesterday = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
    const localStorageDate = new Date(lastUpdatedDebts);
    if (!localStorageDate || localStorageDate < yesterday) {
      setDebts([]);
      setLastUpdatedDebts(new Date());
    }
  }, []);

  return (
    <div
      className={cx('DebtCalculator', {
        DebtCalculatorIndex: isIndex,
      })}
    >
      <div className="DebtCalculatorHeading" ref={ref}>
        <Container row justify="center">
          <Container grid={9} space={2}>
            <article>
              <h1>{heading}</h1>
              <p>{subheading}</p>
            </article>
          </Container>
        </Container>
      </div>
      {isIndex ? (
        <div>
          <Paper theme="medium" pad={isScreenSize('sm') ? { top: 3, bottom: 3 } : isEditing ? 5 : 0}>
            <Container className={cx({ DebtCalculatorIndexSelect: isIndex && !isEditing })}>
              <Paper theme={isEditing ? 'elevated' : 'medium'} pad={isScreenSize('sm') ? 1 : 5}>
                <DebtFormController
                  onDebtSubmit={onDebtSubmit}
                  title="To get started, select a debt you carry:"
                  buttonText="Add my debt"
                  onSelectDebtType={setIsEditing}
                  debts={debts}
                  allowCancel={isIndex}
                />
              </Paper>
              <div className="DebtCalculatorDisclaimerContainer">{isEditing && <DebtDisclaimer />}</div>
            </Container>
            {!isEditing && <figure className="DebtCalculatorBgImage"></figure>}
          </Paper>
        </div>
      ) : isFinished && resultsCookie ? (
        <DebtResults
          debts={debts}
          totalBalance={totalBalance}
          totalInterestPayments={totalInterestPayments}
          totalMonthlyPayment={totalMonthlyPayment}
          payOffDate={payOffDate}
        />
      ) : (
        <div>
          <Container align="center" pad={0} className="DebtCalculatorTwoColumnChart">
            <TwoColumnChart
              debts={debts}
              onDebtSubmit={onDebtSubmit}
              onDebtDelete={onDebtDelete}
              isSticky={!isVisible}
              onFinish={handleFinishClick}
              totalBalance={totalBalance}
              payOffDate={payOffDate}
              showDisclaimer={!isIndex}
            />
          </Container>

          <Paper theme="medium" pad={{ bottom: 6 }}>
            <DebtCTA />
          </Paper>
        </div>
      )}
    </div>
  );
};

export default DebtCalculatorController;
